// Import from 'lit' (not 'lit-element') to match kerika-pwa.js — mixing the
// two installed packages renders templates as "[object Object]".
import { html, css } from 'lit';

// 2. Store
import { store } from '../../store.js';

// 3. Redux modules
import * as app from '../../redux/app';
import * as board from '../../redux/board';

// 7. Child components
import '../kerika-icon.js';

/**
 * Chrome-for-Android-style pull-to-refresh, wired once on
 * [kerika-pwa](../kerika-pwa.js). Mobile only. On commit, calls
 * `window.location.reload()` directly — no Redux action.
 *
 * See: wiki/features/pull-to-refresh.md
 */


// Defaults for `app.config.pullToRefresh` — fallback when a key is absent.

// shown = finger pulled * dragResistance
const DRAG_RESISTANCE = 0.5;

// release past this distance to commit
const ACTIVATION_THRESHOLD_PX = 80;

// indicator stops following finger past this
const MAX_PULL_PX = 120;

// shown px where the icon reaches full opacity
const OPACITY_FULL_PX = 20;

// reverse-swipe past peak by this much to cancel a commit
const CANCEL_REVERSE_SWIPE_PX = 80;

// brief delay before reload so the user sees the refreshing state
const REFRESH_DELAY_MS = 400;

// --- Internal-only (not exposed via config) ---

// movement before vertical-vs-horizontal direction is locked in
const DIRECTION_LOCK_PX = 6;

// indicator height; parked flush above the viewport so 1px pull shows a sliver
const INDICATOR_PARK_PX = 40;

// indicator's Y while refreshing — visibly above the activation position
const REFRESHING_TRANSLATE_Y_PX = 16;

export const pullToRefreshMixin = (baseElement) => class extends baseElement {
  static get styles() {
      return [
        css`
          .ptr-indicator {
            align-items: center;
            background-color: var(--mdc-theme-surface, #fff);
            border-radius: 50%;
            box-shadow:
              0 3px 6px rgba(0, 0, 0, 0.16),
              0 3px 6px rgba(0, 0, 0, 0.23);
            display: flex;
            height: 40px;
            justify-content: center;
            left: 50%;
            opacity: 1;
            pointer-events: none;
            position: fixed;
            top: -4px;
            transform: translateX(-50%) translateY(-40px);
            width: 40px;
            will-change: transform;
            z-index: 10000;
          }

          .ptr-indicator kerika-icon {
            --dw-icon-color: var(--primary-color);
            --dw-icon-size: 24px;
            clip-path: inset(0 0 100% 0);
            opacity: 0;
            transform: rotate(0deg);
            will-change: opacity, transform, clip-path;
          }

          .ptr-indicator[refreshing] kerika-icon {
            clip-path: none;
            opacity: 1;
          }
        `,
      ];
    }

    constructor() {
      super();
      this._boundPtrPointerDown = this._ptrOnPointerDown.bind(this);
      this._boundPtrPointerMove = this._ptrOnPointerMove.bind(this);
      this._boundPtrPointerUp = this._ptrOnPointerUp.bind(this);
      this._boundPtrPointerCancel = this._ptrOnPointerCancel.bind(this);
      this._boundPtrTouchMove = this._ptrOnTouchMove.bind(this);

      // Non-reactive — updates don't trigger a Lit render. The indicator is
      // mutated imperatively from _ptrUpdateIndicator().
      this._ptrArmed = false;
      this._ptrActive = false;
      this._ptrShownPx = 0;
      this._ptrStartY = 0;
      this._ptrStartX = 0;
      this._ptrPointerId = null;
      this._ptrIndicatorEl = null;
      this._ptrIconEl = null;
      this._ptrMaxShownPx = 0;
      this._ptrCancelled = false;
      this._ptrRefreshing = false;
      this._ptrReloadTimeoutId = null;
      this._ptrSpinAnimation = null;

      // Cached on each arming pointerdown, reused for the rest of the gesture.
      this._ptrCfg = null;
    }

    connectedCallback() {
      super.connectedCallback && super.connectedCallback();
      // Listen on `document`, not `this`. Some dialogs (`dw-fit-dialog`)
      // portal their content directly to `document.body`, making them
      // SIBLINGS of `kerika-pwa` — pointer events inside them never bubble
      // up to the host. Listening on `document` catches gestures anywhere.
      document.addEventListener('pointerdown', this._boundPtrPointerDown);
      document.addEventListener('pointermove', this._boundPtrPointerMove);
      document.addEventListener('pointerup', this._boundPtrPointerUp);
      document.addEventListener('pointercancel', this._boundPtrPointerCancel);
      document.addEventListener('touchmove', this._boundPtrTouchMove, { passive: false });

      // Deliberately no `overscroll-behavior` override here: index.html already sets `none`
      // on <html> and <body>, which stops the browser's own pull-to-refresh AND the
      // overscroll bounce. Relaxing it to `contain` brings the bounce back and the page
      // rubber-bands under a card drag.
    }

    disconnectedCallback() {
      super.disconnectedCallback && super.disconnectedCallback();
      document.removeEventListener('pointerdown', this._boundPtrPointerDown);
      document.removeEventListener('pointermove', this._boundPtrPointerMove);
      document.removeEventListener('pointerup', this._boundPtrPointerUp);
      document.removeEventListener('pointercancel', this._boundPtrPointerCancel);
      document.removeEventListener('touchmove', this._boundPtrTouchMove, { passive: false });
      this._ptrResetState();
      if (this._ptrReloadTimeoutId !== null) {
        clearTimeout(this._ptrReloadTimeoutId);
        this._ptrReloadTimeoutId = null;
      }
      if (this._ptrSpinAnimation) {
        this._ptrSpinAnimation.cancel();
        this._ptrSpinAnimation = null;
      }
    }

    firstUpdated(changedProps) {
      super.firstUpdated && super.firstUpdated(changedProps);
      this._ptrIndicatorEl = this.renderRoot.querySelector('.ptr-indicator');
      this._ptrIconEl = this.renderRoot.querySelector('.ptr-indicator kerika-icon');
    }

    /** Static template; visual state is written imperatively from _ptrUpdateIndicator(). @protected */
    get _pullToRefreshIndicatorTemplate() {
      return html`
        <div class="ptr-indicator" aria-hidden="true">
          <kerika-icon name="refresh"></kerika-icon>
        </div>
      `;
    }

    _ptrOnPointerDown(event) {
      // touch only — ignore mouse/pen
      if (event.pointerType !== 'touch') return;

      // page is about to reload; ignore further touches
      if (this._ptrRefreshing) return;

      // single-touch only — a second finger would hijack tracking
      if (this._ptrPointerId !== null) return;

      // mobile only
      if (!app.selectors.isMobileDevice(store.getState())) return;

      // a card/column drag owns the gesture
      if (board.selectors.dragStarted(store.getState())) return;

      const container = this._ptrFindScrollContainer(event);
      if (!container || container.scrollTop > 0) return;

      this._ptrArmed = true;
      this._ptrStartY = event.clientY;
      this._ptrStartX = event.clientX;
      this._ptrPointerId = event.pointerId;
      this._ptrCfg = this._ptrGetConfig();
    }

    _ptrOnPointerMove(event) {
      if (!this._ptrArmed) return;
      if (event.pointerId !== this._ptrPointerId) return;

      // The long-press that starts a drag lands well after the pointerdown that armed us, so
      // this mid-gesture check is the one that matters: without it a card dragged down from
      // the top of a column pulls the indicator too, and releasing reloads mid-drag.
      if (board.selectors.dragStarted(store.getState())) {
        this._ptrResetState();
        this._ptrUpdateIndicator();
        return;
      }

      const deltaY = event.clientY - this._ptrStartY;
      const deltaX = event.clientX - this._ptrStartX;

      // Direction-lock: bail on horizontal swipes (let column carousels work)
      // or upward swipes (user is scrolling the page).
      if (!this._ptrActive) {
        const movement = Math.max(Math.abs(deltaY), Math.abs(deltaX));
        if (movement < DIRECTION_LOCK_PX) return;
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          this._ptrResetState();
          this._ptrUpdateIndicator();
          return;
        }
        if (deltaY < 0) {
          this._ptrResetState();
          this._ptrUpdateIndicator();
          return;
        }
      }

      // Finger went back above start: retract indicator but stay armed so a
      // re-pull can still commit (separate from the reverse-swipe cancel rule).
      if (deltaY <= 0) {
        this._ptrShownPx = 0;
        this._ptrActive = false;
        this._ptrUpdateIndicator();
        return;
      }

      const cfg = this._ptrCfg;
      const shown = Math.min(deltaY * cfg.dragResistance, cfg.maxPullPx);
      this._ptrShownPx = shown;
      if (shown > this._ptrMaxShownPx) this._ptrMaxShownPx = shown;

      // After crossing threshold, reverse-swiping past peak by
      // cancelReverseSwipePx → release won't commit. Sticky for the rest
      // of the gesture.
      if (
        !this._ptrCancelled &&
        this._ptrMaxShownPx >= cfg.activationThresholdPx &&
        shown <= this._ptrMaxShownPx - cfg.cancelReverseSwipePx
      ) {
        this._ptrCancelled = true;
      }

      if (!this._ptrActive) {
        this._ptrActive = true;
        // Capture the pointer so descendants (lazy-list, dw-swipe, etc.) can't
        // steal subsequent move/up events. Done only after direction-lock so
        // taps on buttons aren't hijacked.
        try {
          this.setPointerCapture(this._ptrPointerId);
        } catch (e) {
          // pointer may already be gone
        }
      }
      this._ptrUpdateIndicator();
    }

    _ptrOnPointerUp(event) {
      if (event.pointerId !== this._ptrPointerId) return;
      const overThreshold = this._ptrShownPx >= this._ptrCfg.activationThresholdPx;
      const shouldCommit = overThreshold && !this._ptrCancelled;
      this._ptrReleasePointer();
      if (shouldCommit) {
        this._ptrCommitRefresh();
      } else {
        this._ptrResetState();
        this._ptrUpdateIndicator();
      }
    }

    /** Enter the refreshing state and schedule the browser reload. @protected */
    _ptrCommitRefresh() {
      this._ptrRefreshing = true;
      if (this._ptrIndicatorEl) {
        this._ptrIndicatorEl.setAttribute('refreshing', '');
      }

      // Capture current rotation BEFORE reset so the spin starts there —
      // no visible snap when the user releases mid-pull. CSS @keyframes
      // can't start from a dynamic value, hence the JS-driven spin.
      let currentRotation = 0;
      if (this._ptrIconEl && this._ptrIconEl.style.transform) {
        const m = this._ptrIconEl.style.transform.match(/rotate\(([-\d.]+)deg\)/);
        if (m) currentRotation = parseFloat(m[1]);
      }

      // Snapshot the delay before reset — _ptrResetState doesn't clear
      // _ptrCfg today, but reading the config after reset would break if
      // that invariant ever changes.
      const refreshDelayMs = this._ptrCfg.refreshDelayMs;

      this._ptrResetState();
      this._ptrUpdateIndicator();

      // WAAPI (not CSS) so the spin can start from `currentRotation`.
      // Note: spin visibly stops when reload() tears down the DOM. No
      // animation API survives navigation — accepted trade-off.
      if (this._ptrIconEl) {
        if (this._ptrSpinAnimation) this._ptrSpinAnimation.cancel();
        this._ptrSpinAnimation = this._ptrIconEl.animate(
          [{ transform: `rotate(${currentRotation}deg)` }, { transform: `rotate(${currentRotation + 360}deg)` }],
          {
            // 500ms = one full rotation inside the visible ~700ms window
            duration: 500,
            iterations: Number.POSITIVE_INFINITY,
            easing: 'linear',
          },
        );
      }

      this._ptrReloadTimeoutId = setTimeout(() => {
        window.location.reload();
      }, refreshDelayMs);
    }

    _ptrOnPointerCancel(event) {
      // System-cancelled touch. Not `lostpointercapture` — that also fires
      // on voluntary release.
      if (event.pointerId !== this._ptrPointerId) return;
      this._ptrReleasePointer();
      this._ptrResetState();
      this._ptrUpdateIndicator();
    }

    /**
     * preventDefault while armed stops the browser from claiming this touch
     * as its own pull-to-refresh. Must be a non-passive listener.
     * @protected
     */
    _ptrOnTouchMove(event) {
      if (this._ptrArmed) {
        event.preventDefault();
      }
    }

    _ptrReleasePointer() {
      if (this._ptrPointerId !== null) {
        try {
          if (this.hasPointerCapture && this.hasPointerCapture(this._ptrPointerId)) {
            this.releasePointerCapture(this._ptrPointerId);
          }
        } catch (e) {
          // pointer may already be gone
        }
      }
    }

    /** Writes transform/opacity directly — no Lit render per pointermove. @protected */
    _ptrUpdateIndicator() {
      // re-capture if firstUpdated() missed it (defensive)
      if (!this._ptrIndicatorEl && this.renderRoot) {
        this._ptrIndicatorEl = this.renderRoot.querySelector('.ptr-indicator');
        this._ptrIconEl = this.renderRoot.querySelector('.ptr-indicator kerika-icon');
      }
      const el = this._ptrIndicatorEl;
      if (!el) return;

      if (this._ptrRefreshing) {
        el.style.transform = `translateX(-50%) translateY(${REFRESHING_TRANSLATE_Y_PX}px)`;
        if (this._ptrIconEl) {
          this._ptrIconEl.style.opacity = '';
          this._ptrIconEl.style.transform = '';
          this._ptrIconEl.style.clipPath = '';
        }
        return;
      }

      const shown = this._ptrActive ? this._ptrShownPx : 0;
      const translateY = shown - INDICATOR_PARK_PX;
      el.style.transform = `translateX(-50%) translateY(${translateY}px)`;

      // Pure functions of `shown` so reverse-swipe naturally rewinds.
      const icon = this._ptrIconEl;
      if (icon) {
        if (shown === 0) {
          // Let the underlying CSS defaults take over — no inline noise.
          icon.style.opacity = '';
          icon.style.transform = '';
          icon.style.clipPath = '';
        } else {
          const cfg = this._ptrCfg;
          const pOpacity = Math.min(shown / cfg.opacityFullPx, 1);
          const pRender = Math.min(shown / cfg.activationThresholdPx, 1);
          const rotation = pRender * 360;
          icon.style.opacity = String(pOpacity);
          icon.style.transform = `rotate(${rotation}deg)`;
          icon.style.clipPath = `inset(0 0 ${(1 - pRender) * 100}% 0)`;
        }
      }
    }

    /**
     * Nearest ancestor that's truly vertically-scrollable. The
     * `scrollHeight > clientHeight` check skips horizontal carousels (e.g.
     * `kerika-column-list`) whose container has `overflow: auto` on both
     * axes but no vertical overflow. Falls back to `document.scrollingElement`.
     * @protected
     */
    _ptrFindScrollContainer(event) {
      const path = event.composedPath();
      for (const el of path) {
        if (!(el instanceof Element)) continue;
        const style = window.getComputedStyle(el);
        const overflowY = style.overflowY;
        const allowsScrollY = overflowY === 'auto' || overflowY === 'scroll';
        if (!allowsScrollY) continue;
        if (el.scrollHeight <= el.clientHeight) continue;
        return el;
      }
      return document.scrollingElement;
    }

    _ptrResetState() {
      this._ptrArmed = false;
      this._ptrActive = false;
      this._ptrShownPx = 0;
      this._ptrStartY = 0;
      this._ptrStartX = 0;
      this._ptrPointerId = null;
      this._ptrMaxShownPx = 0;
      this._ptrCancelled = false;
      // _ptrRefreshing stays true until reload tears down the DOM.
      // _ptrCfg is overwritten on the next pointerdown.
    }

    /**
     * Reads tunables from `app.config.pullToRefresh` with module-constant
     * fallbacks. Firebase overrides are already merged in by the config selector.
     * @protected
     */
    _ptrGetConfig() {
      const cfg = (app.selectors.config(store.getState()) || {}).pullToRefresh || {};
      const pick = (v, fallback) => (typeof v === 'number' && !isNaN(v) ? v : fallback);
      return {
        dragResistance: pick(cfg.dragResistance, DRAG_RESISTANCE),
        activationThresholdPx: pick(cfg.activationThresholdPx, ACTIVATION_THRESHOLD_PX),
        maxPullPx: pick(cfg.maxPullPx, MAX_PULL_PX),
        opacityFullPx: pick(cfg.opacityFullPx, OPACITY_FULL_PX),
        cancelReverseSwipePx: pick(cfg.cancelReverseSwipePx, CANCEL_REVERSE_SWIPE_PX),
        refreshDelayMs: pick(cfg.refreshDelayMs, REFRESH_DELAY_MS),
      };
    }
  };
